import React from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

const QuestionSection = ({ category, responses, onResponseChange }) => {
  const renderQuestion = (question) => {
    const currentResponse = responses[question.id];

    const handleResponseChange = (value) => {
      onResponseChange(question.id, value);
    };

    const renderLikertScale = (question) => {
      const labels = question.scaleLabels || {
        1: 'Strongly Disagree',
        2: 'Disagree',
        3: 'Neutral',
        4: 'Agree',
        5: 'Strongly Agree'
      };

      const scaleSize = Object.keys(labels).length;

      return (
        <div className="space-y-4">
          <div className="bg-secondary-50/70 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-secondary-200/50">
            {/* Mobile: vertical stack of tap targets */}
            <div className="space-y-2 sm:hidden">
              {Array.from({ length: scaleSize }, (_, i) => i + 1).map((value) => {
                const checked = currentResponse === value.toString();
                return (
                  <label
                    key={value}
                    htmlFor={`${question.id}-mobile-${value}`}
                    className={`flex items-center gap-3 px-3 py-3 bg-white border rounded-lg cursor-pointer transition-colors ${
                      checked
                        ? 'border-primary-400 ring-2 ring-primary-200'
                        : 'border-secondary-200 hover:bg-secondary-50 active:bg-secondary-100'
                    }`}
                  >
                    <input
                      type="radio"
                      id={`${question.id}-mobile-${value}`}
                      name={question.id}
                      value={value}
                      checked={checked}
                      onChange={(e) => handleResponseChange(e.target.value)}
                      className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-secondary-300 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-sm font-semibold text-secondary-800 w-6 flex-shrink-0">
                      {value}
                    </span>
                    <span className="text-sm font-medium text-secondary-700 leading-snug">
                      {labels[value]}
                    </span>
                  </label>
                );
              })}
            </div>

            {/* Desktop / tablet: horizontal scale with labels above */}
            <div className="hidden sm:flex justify-between items-center">
              {Array.from({ length: scaleSize }, (_, i) => i + 1).map((value) => (
                <div key={value} className="flex flex-col items-center space-y-2">
                  <label
                    htmlFor={`${question.id}-${value}`}
                    className="text-xs font-medium text-secondary-600 text-center leading-tight cursor-pointer hover:text-secondary-800 transition-colors"
                  >
                    {labels[value]}
                  </label>
                  <div className="relative">
                    <input
                      type="radio"
                      id={`${question.id}-${value}`}
                      name={question.id}
                      value={value}
                      checked={currentResponse === value.toString()}
                      onChange={(e) => handleResponseChange(e.target.value)}
                      className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-secondary-300 cursor-pointer"
                    />
                    {currentResponse === value.toString() && (
                      <div className="absolute -inset-1 rounded-full border-2 border-primary-400 animate-pulse"></div>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-secondary-800">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    const renderYesNo = () => {
      return (
        <div className="bg-secondary-50/70 backdrop-blur-sm rounded-xl p-4 border border-secondary-200/50">
          <div className="flex space-x-8 justify-center">
            <label className="flex items-center cursor-pointer group">
              <div className="relative">
                <input
                  type="radio"
                  name={question.id}
                  value="yes"
                  checked={currentResponse === 'yes'}
                  onChange={(e) => handleResponseChange(e.target.value)}
                  className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-secondary-300 cursor-pointer"
                />
                {currentResponse === 'yes' && (
                  <div className="absolute -inset-1 rounded-full border-2 border-primary-400 animate-pulse"></div>
                )}
              </div>
              <span className="ml-3 text-sm font-medium text-secondary-700 group-hover:text-secondary-900">Yes</span>
            </label>
            <label className="flex items-center cursor-pointer group">
              <div className="relative">
                <input
                  type="radio"
                  name={question.id}
                  value="no"
                  checked={currentResponse === 'no'}
                  onChange={(e) => handleResponseChange(e.target.value)}
                  className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-secondary-300 cursor-pointer"
                />
                {currentResponse === 'no' && (
                  <div className="absolute -inset-1 rounded-full border-2 border-primary-400 animate-pulse"></div>
                )}
              </div>
              <span className="ml-3 text-sm font-medium text-secondary-700 group-hover:text-secondary-900">No</span>
            </label>
          </div>
        </div>
      );
    };

    const renderTextInput = () => {
      const maxLength = 2000;
      const currentLength = currentResponse ? currentResponse.length : 0;
      const remainingChars = maxLength - currentLength;

      return (
        <div className="bg-secondary-50/70 backdrop-blur-sm rounded-xl p-4 border border-secondary-200/50">
          <textarea
            value={currentResponse || ''}
            onChange={(e) => {
              if (e.target.value.length <= maxLength) {
                handleResponseChange(e.target.value);
              }
            }}
            rows={4}
            maxLength={maxLength}
            className="w-full bg-white border border-secondary-300 rounded-lg px-4 py-3 text-secondary-700 placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            placeholder="Please provide your detailed answer..."
          />
          <div className="flex justify-between items-center mt-2 text-xs">
            <span className="text-secondary-500">
              💡 Take your time to provide a thoughtful response
            </span>
            <span className={`font-medium ${
              remainingChars < 100 ? 'text-accent-600' :
              remainingChars < 50 ? 'text-error' : 'text-secondary-500'
            }`}>
              {currentLength} / {maxLength} characters
              {remainingChars < 100 && remainingChars > 0 && (
                <span className="ml-1">({remainingChars} remaining)</span>
              )}
            </span>
          </div>
        </div>
      );
    };

    const renderMultipleChoice = () => {
      const questionOptions = question.options || question.optio;

      if (!questionOptions || questionOptions.length === 0) {
        return <div className="text-error text-sm">No options configured for this question</div>;
      }

      return (
        <div className="bg-secondary-50/70 backdrop-blur-sm rounded-xl p-4 border border-secondary-200/50">
          <div className="space-y-3">
            {questionOptions.map((option, index) => (
              <label key={index} className="flex items-center cursor-pointer group">
                <div className="relative">
                  <input
                    type="radio"
                    name={question.id}
                    value={option}
                    checked={currentResponse === option}
                    onChange={(e) => handleResponseChange(e.target.value)}
                    className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-secondary-300 cursor-pointer"
                  />
                  {currentResponse === option && (
                    <div className="absolute -inset-1 rounded-full border-2 border-primary-400 animate-pulse"></div>
                  )}
                </div>
                <div className="ml-3 flex items-center">
                  <span className="text-sm font-medium text-secondary-600 mr-2">
                    {String.fromCharCode(65 + index)}.
                  </span>
                  <span className="text-sm font-medium text-secondary-700 group-hover:text-secondary-900">
                    {option}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>
      );
    };

    const renderMultiSelect = () => {
      const questionOptions = question.options || question.optio;

      if (!questionOptions || questionOptions.length === 0) {
        return <div className="text-error text-sm">No options configured for this question</div>;
      }

      let selectedOptions = [];
      try {
        selectedOptions = currentResponse ? JSON.parse(currentResponse) : [];
        if (!Array.isArray(selectedOptions)) selectedOptions = [];
      } catch (e) {
        selectedOptions = [];
      }

      const handleMultiSelectChange = (option, isChecked) => {
        let newSelection = [...selectedOptions];

        if (isChecked) {
          if (!newSelection.includes(option)) {
            newSelection.push(option);
          }
        } else {
          newSelection = newSelection.filter(item => item !== option);
        }

        handleResponseChange(JSON.stringify(newSelection));
      };

      return (
        <div className="bg-secondary-50/70 backdrop-blur-sm rounded-xl p-4 border border-secondary-200/50">
          <div className="mb-3">
            <span className="text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
              ✓ Select all that apply
            </span>
          </div>
          <div className="space-y-3">
            {questionOptions.map((option, index) => (
              <label key={index} className="flex items-center cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={selectedOptions.includes(option)}
                    onChange={(e) => handleMultiSelectChange(option, e.target.checked)}
                    className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded cursor-pointer"
                  />
                  {selectedOptions.includes(option) && (
                    <div className="absolute -inset-1 rounded border-2 border-primary-400 animate-pulse"></div>
                  )}
                </div>
                <div className="ml-3 flex items-center">
                  <span className="text-sm font-medium text-secondary-600 mr-2">
                    {String.fromCharCode(65 + index)}.
                  </span>
                  <span className="text-sm font-medium text-secondary-700 group-hover:text-secondary-900">
                    {option}
                  </span>
                </div>
              </label>
            ))}
          </div>
          {selectedOptions.length > 0 && (
            <div className="mt-4 pt-3 border-t border-secondary-200">
              <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded">
                {selectedOptions.length} option{selectedOptions.length !== 1 ? 's' : ''} selected
              </span>
            </div>
          )}
        </div>
      );
    };

    const renderResponseInput = () => {
      const responseType = question.responseType || question.respo || question.type;

      switch (responseType) {
        case 'likert_5':
        case 'likert_7':
        case 'likert':
          return renderLikertScale(question);
        case 'multiple_choice':
          return renderMultipleChoice();
        case 'multi_select':
          return renderMultiSelect();
        case 'yes_no':
        case 'boolean':
          return renderYesNo();
        case 'text':
          return renderTextInput();
        default:
          return renderLikertScale(question);
      }
    };

    return (
      <div
        key={question.id}
        className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-secondary-200/50 p-4 sm:p-6 mb-6 hover:shadow-xl transition-all duration-300"
      >
        <div className="mb-6">
          <div className="flex items-start justify-between">
            <h3 className="text-lg font-semibold text-secondary-800 pr-4 leading-relaxed">
              {question.text}
              {question.isRequired && (
                <span className="text-error ml-1">*</span>
              )}
            </h3>
            {!currentResponse && question.isRequired && (
              <div className="bg-accent-100/70 backdrop-blur-sm rounded-full p-1.5 border border-accent-200/50">
                <ExclamationCircleIcon className="h-4 w-4 text-accent-600 flex-shrink-0" />
              </div>
            )}
          </div>

          {question.description && (
            <p className="text-sm text-secondary-600 mt-3 leading-relaxed">
              {question.description}
            </p>
          )}
        </div>

        <div className="mb-5">
          {renderResponseInput()}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-secondary-100">
          <div>
            {question.isRequired && (
              <span className="text-xs font-medium text-secondary-500 bg-secondary-100 px-2 py-1 rounded-full">Required</span>
            )}
          </div>
          <div>
            {currentResponse ? (
              <span className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full flex items-center">
                <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></span>
                Answered
              </span>
            ) : (
              <span className="text-xs font-medium text-secondary-400 bg-secondary-50 px-3 py-1 rounded-full">Not answered</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!category || !category.questions) {
    return <div className="text-center py-8 text-secondary-600">No questions available</div>;
  }

  return (
    <div className="space-y-6">
      {category.questions
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(renderQuestion)}
    </div>
  );
};

export default QuestionSection;
